'use client'

import menuLayanan from '@/shared/jsons/menu-layanan.json'
import menu from '@/shared/jsons/menu.json'
import ExportedImage from 'next-image-export-optimizer'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { CgChevronDown } from 'react-icons/cg'
import { MdChevronRight, MdMenu, MdSearch } from 'react-icons/md'

import { Button } from '@/components/atoms/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/ui/popover'
import Container from '@/components/templates/container/container'

import { cn } from '@/lib/utils'

const NavbarMobile = dynamic(() => import('./navbar-mobile').then((mod) => mod.NavbarMobile), {
  ssr: false
})

type Menu = {
  label: string
  url: string
}

interface NavbarProps {
  serviceAble?: boolean
  searchAble?: boolean
  language?: 'id' | 'en'
}

export const Navbar = ({ serviceAble = false, searchAble = true, language = 'id' }: NavbarProps) => {
  const menuActive = language === 'en' ? menu.en : menu.id
  const menuLayananActive = language === 'en' ? menuLayanan.en : menuLayanan.id

  const pathname = usePathname()
  const params = useSearchParams()
  const [query, setQuery] = useState<string>(params.get('query') ?? '')
  const router = useRouter()

  const [openStates, setOpenStates] = useState(new Array(menuActive.length).fill(false))
  const [expandModal, setExpandModal] = useState<Menu[]>()
  const [navOpen, setNavOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [showService, setShowService] = useState(false)

  useEffect(() => {
    const hash = window.location.hash
    const offset = 70

    if (hash) {
      const elementId = hash.substring(1)
      const element = document.getElementById(elementId)

      if (element) {
        const topPosition = element.getBoundingClientRect().top + window.scrollY - offset
        window.scrollTo({ top: topPosition, behavior: 'smooth' })
      }
    } else {
      window.scrollTo(0, 0)
    }
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY
      const ourClientElement = document.getElementById('our-client')
      const windowHeight = window.innerHeight

      if (ourClientElement) {
        const ourClientOffset = ourClientElement.offsetTop

        const showOffset = ourClientOffset - windowHeight / 2

        setShowService(offset > showOffset)
      } else {
        setShowService(offset > 1)
      }
    }

    if (serviceAble) {
      window.addEventListener('scroll', handleScroll)

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [serviceAble])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      router.push(`/search?query=${encodeURIComponent(query)}`)
    }
  }

  return (
    <header>
      <nav className="fixed top-0 z-50 w-full bg-white border-b border-gray-300">
        <Container className="z-10 flex items-center justify-between py-1.5 lg:py-2 gap-x-5">
          <div className="flex items-center lg:gap-x-14 lg:w-[55%] w-full justify-between lg:justify-normal">
            <button
              onClick={() => setNavOpen(true)}
              className="text-primary-blue lg:hidden"
              aria-label="Navigation Menu Open"
            >
              <MdMenu size={36} />
            </button>
            <Link href="/" className="relative">
              <ExportedImage
                src="/easybiz-hoc.png"
                loading="eager"
                priority={true}
                alt="Logo Easybiz"
                unoptimized
                width={82}
                height={82}
                sizes="(max-width: 768px) 60px, (max-width: 1200px) 82px, 82px"
                className="object-contain size-[60px] lg:size-20"
              />
            </Link>
            {searchAble && (
              <button
                onClick={() => setSearchOpen(true)}
                className="text-primary-blue lg:hidden"
                aria-label="Navigation Search"
              >
                <MdSearch size={32} />
              </button>
            )}

            <ul className="items-center hidden font-medium text-black lg:flex">
              {menuActive.map((item, index) => {
                if (item.childs) {
                  return (
                    <li
                      key={item.label}
                      onMouseEnter={() => {
                        const newOpenStates = [...openStates]
                        newOpenStates[index] = true
                        setOpenStates(newOpenStates)
                      }}
                      onMouseLeave={() => {
                        const newOpenStates = [...openStates]
                        newOpenStates[index] = false
                        setOpenStates(newOpenStates)
                        setExpandModal([])
                      }}
                    >
                      <Popover open={openStates[index]}>
                        <PopoverTrigger
                          className={cn(
                            'flex hover:text-primary items-center gap-0.5 pl-3 pr-2 py-3 outline-none',
                            pathname == item.url ? 'text-primary-blue' : 'text-black'
                          )}
                        >
                          <p>{item.label}</p>
                          <CgChevronDown className={cn('inline w-5 h-5', openStates[index] && 'rotate-180')} />
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="relative flex flex-col w-auto py-5 -mt-1 border rounded-lg shadow-lg"
                        >
                          <div className="flex w-auto">
                            <ul className="font-medium relative z-20 w-[380px] pr-2 space-y-2">
                              {item.childs.map((child, index) => {
                                if (child.childs) {
                                  return (
                                    <li key={index}>
                                      <button
                                        onClick={() => router.push(child.url)}
                                        onMouseEnter={() => setExpandModal(child.childs)}
                                        className={cn(
                                          'flex text-start items-center justify-between w-full p-3 rounded cursor-pointer gap-x-2 outline-none',
                                          expandModal == child.childs
                                            ? 'text-primary-blue bg-blue-50'
                                            : 'text-black hover:bg-blue-50 hover:text-primary-blue'
                                        )}
                                      >
                                        {child.label}
                                        <MdChevronRight size={24} />
                                      </button>
                                    </li>
                                  )
                                }

                                return (
                                  <li key={index} className="w-full" onMouseEnter={() => setExpandModal([])}>
                                    <Link
                                      href={child.url}
                                      className="flex items-center justify-between w-full p-3 text-black rounded gap-x-2 hover:bg-blue-50 hover:text-primary-blue"
                                    >
                                      {child.label}
                                    </Link>
                                  </li>
                                )
                              })}
                            </ul>

                            <ul
                              className={cn(
                                'transition-all duration-500',
                                expandModal?.length
                                  ? 'font-medium pl-2 space-y-3 border-l border-gray-300 opacity-100 w-[380px]'
                                  : 'opacity-0'
                              )}
                            >
                              {expandModal?.map((item, index) => (
                                <li key={index} className="w-full">
                                  <Link
                                    href={item.url}
                                    className="flex items-center justify-between w-full p-3 text-black rounded gap-x-2 hover:bg-blue-50 hover:text-primary-blue"
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </li>
                  )
                }

                return (
                  <li
                    key={index}
                    className={cn('hover:text-primary-blue', pathname == item.url ? 'text-primary-blue' : 'text-black')}
                  >
                    <Link href={item.url} className="p-3">
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="hidden lg:flex items-center justify-end gap-x-3 lg:w-[45%]">
            {searchAble && (
              <form className="flex items-center w-full border border-r-0 border-gray-300 rounded" onSubmit={handleSearch}>
                <input
                  defaultValue={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    language === 'en' ? 'Search products, article, or regulations' : 'Cari layanan, artikel atau peraturan'
                  }
                  className="w-full h-12 px-5 text-gray-900 bg-transparent border-0 outline-none placeholder:text-gray-500 ring-0"
                />
                <Button aria-label="Button Search" className="h-12 text-white rounded rounded-l-none" type="submit">
                  <MdSearch size={24} />
                </Button>
              </form>
            )}

            <Button
              url="/service/corporate-solution"
              variant="secondary"
              className="h-12 text-base font-semibold shadow text-primary-blue gtm-view-service"
            >
              {language === 'en' ? 'Subscribe' : 'Berlangganan'}
            </Button>
          </div>
        </Container>
      </nav>
      {serviceAble && (
        <nav
          className={cn(
            'fixed py-4 hidden lg:block w-full text-black bg-white transition-all z-40 duration-700 border-b border-gray-300',
            showService ? 'translate-y-0 z-30' : '-translate-y-full',
            language === 'id' ? 'text-sm lg:text-[13px]' : 'text-sm lg:text-xs 2xl:text-sm'
          )}
        >
          <Container>
            <ul className="flex flex-wrap justify-between flex-1 gap-2 font-medium">
              {menuLayananActive.map((menu, index) => (
                <li key={index} className={cn('hover:text-primary-blue', pathname == menu.url && 'text-primary-blue')}>
                  <Link href={menu.url}>{menu.label}</Link>
                </li>
              ))}
            </ul>
          </Container>
        </nav>
      )}

      <NavbarMobile
        menu={menuActive}
        menuLayanan={menuLayananActive}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        searchAble={searchAble}
        params={params.get('query') as string}
        language={language}
      />
    </header>
  )
}
